/**
 * src/routes/ProtectedRoute.jsx
 * 
 * Role-based route guard using sessionStorage.
 * Replaces any old localStorage-based auth checks.
 */
import React from "react";
import { Navigate } from "react-router-dom";

// ── Read from sessionStorage (per-tab) ───────────────────────────
export const getStoredUser  = () => {
  try { return JSON.parse(sessionStorage.getItem("platform_user") || "{}"); }
  catch { return {}; }
};
export const getStoredToken = () => sessionStorage.getItem("platform_token");
export const isLoggedIn     = () => !!sessionStorage.getItem("platform_token");

// ── Role groups ───────────────────────────────────────────────────
const ADMIN_ROLES    = ["company_admin"];
const MANAGER_ROLES  = ["manager","senior_manager","team_lead","cto","ceo","vp"];
const EMPLOYEE_ROLES = ["employee","team_lead","manager","senior_manager","cto","ceo","vp"];
const SUPER_ROLES    = ["super_admin"];

// ── Guards ────────────────────────────────────────────────────────

/** Only super admin can access */
export function SuperAdminRoute({ children }) {
  const user = getStoredUser();
  if (!isLoggedIn())                         return <Navigate to="/login" replace/>;
  if (!SUPER_ROLES.includes(user.role))      return <Navigate to="/login" replace/>;
  return children;
}

/**
 * True once a session belongs to an ATS-only company (hrms_enabled===false).
 * Clears the stale session and bounces to /login rather than rendering
 * anything HRMS-shaped — mirrors the block Login.jsx does at sign-in time,
 * for sessions that predate that check or whose company was switched to
 * ATS-only after login.
 */
function blockedFromHrms(user) {
  return user.role !== "super_admin" && user.hrms_enabled === false;
}

/** Only company_admin can access HR/Admin portal */
export function AdminRoute({ children }) {
  const user = getStoredUser();
  if (!isLoggedIn())                         return <Navigate to="/login" replace/>;
  if (blockedFromHrms(user))                 { sessionStorage.clear(); return <Navigate to="/login" replace/>; }
  if (user.role === "super_admin")           return <Navigate to="/super-admin/companies" replace/>;
  if (!ADMIN_ROLES.includes(user.role))      return <Navigate to="/employee/dashboard" replace/>;
  return children;
}

/** All employees (including managers) access employee portal */
export function EmployeeRoute({ children }) {
  const user = getStoredUser();
  if (!isLoggedIn())                         return <Navigate to="/login" replace/>;
  if (blockedFromHrms(user))                 { sessionStorage.clear(); return <Navigate to="/login" replace/>; }
  if (user.role === "super_admin")           return <Navigate to="/super-admin/companies" replace/>;
  if (user.role === "company_admin")         return <Navigate to="/admin/dashboard" replace/>;
  if (!EMPLOYEE_ROLES.includes(user.role))   return <Navigate to="/login" replace/>;
  return children;
}

/** Any authenticated user */
export function PrivateRoute({ children }) {
  if (!isLoggedIn()) return <Navigate to="/login" replace/>;
  return children;
}

export default PrivateRoute;